import dayjs from 'dayjs';
import type { OptimizerJobRow } from './types';
import { type optimizerMaterializationProgress, formatPct01Number } from './helpers';

interface JobRunProgressProps {
    selectedJob: OptimizerJobRow;
    selectedJobProgress: ReturnType<typeof optimizerMaterializationProgress>;
}

const heartbeatToneClass = (status: OptimizerJobRow['workerLivenessStatus']): string => {
    switch (status) {
        case 'stalled':
            return 'text-red-400';
        case 'recovering':
            return 'text-yellow-400';
        case 'active':
        case null:
        case undefined:
            return 'text-zinc-500';
    }
};

/** Progress card shown while a job is queued or actively materializing/searching/validating. */
const JobRunProgress = ({ selectedJob, selectedJobProgress }: JobRunProgressProps) => {
    const { phaseLabel, percent, detail } = selectedJobProgress;
    const recoveryCount = selectedJob.workerRecoveryCount ?? 0;

    return (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 px-4 py-3 mb-4">
            <div className="flex items-baseline justify-between gap-3 mb-2">
                <span className="text-sm font-medium text-white">{phaseLabel}</span>
                <span className="text-sm text-zinc-300">{percent !== null ? formatPct01Number(percent, 0) : 'Working…'}</span>
            </div>
            <div aria-hidden className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                {percent !== null ? (
                    <div className="h-full rounded-full bg-cyan-500 transition-all" style={{ width: `${Math.round(percent * 100)}%` }} />
                ) : (
                    <div className="h-full w-1/3 rounded-full bg-cyan-500/60 animate-pulse" />
                )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-500 mt-2">
                <span>{detail}</span>
                <span className={heartbeatToneClass(selectedJob.workerLivenessStatus)}>
                    {selectedJob.heartbeatAt ? `Last heartbeat ${dayjs(selectedJob.heartbeatAt).fromNow()}` : 'No heartbeat yet'}
                    {recoveryCount > 0 ? ` · ${recoveryCount.toLocaleString()} recoveries` : ''}
                </span>
            </div>
        </div>
    );
};

export default JobRunProgress;
